import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import {
  Academy,
  AgeGroup,
  AgeGroupMethod,
  AgeGroupFormData,
  ageGroupFormSchema,
  TENNIS_BALL_COLOR_PRESETS,
  matchTennisBallColorPreset,
  ageRangeIncludesAdultAges,
  resolveAgeRangeCalendarColor,
  type TennisBallColorPresetKey,
} from '@court-sync/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Modal,
  Switch,
} from '../ui';
import { ageGroupService } from '../../services/ageGroup';
import { invalidateAgeGroupsQueries } from '../../queries/ageGroups';
import { Pencil, Trash2, Plus, ChevronUp, ChevronDown, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AgeGroupsSettingsTabProps {
  academy: Academy;
}

const METHOD_LABELS: Record<AgeGroupMethod, string> = {
  age_range: 'Age Range',
  ball_color: 'Ball Color',
  custom: 'Custom',
};

const DEFAULT_CALENDAR_COLOR = '#64748b';

const SEED_METHOD_OPTIONS: { value: AgeGroupMethod; label: string; description: string }[] = [
  {
    value: 'age_range',
    label: 'Age Range',
    description: 'Groups like "Mini (4-6)", "Junior (7-10)", "Teen (11-17)", "Adult (18+)"',
  },
  {
    value: 'ball_color',
    label: 'Ball Color',
    description: 'Ball-colour pathway labels with preset calendar colours',
  },
  {
    value: 'custom',
    label: 'Custom Labels',
    description: 'Start with a few example custom labels you can rename',
  },
];

function AgeGroupMethodBadge({ method }: { method: AgeGroupMethod }) {
  const variantClasses: Record<AgeGroupMethod, string> = {
    age_range: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    ball_color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
    custom: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        variantClasses[method]
      )}
    >
      {METHOD_LABELS[method]}
    </span>
  );
}

interface AgeGroupFormProps {
  defaultValues?: Partial<AgeGroupFormData>;
  onSubmit: (data: AgeGroupFormData) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

function AgeGroupForm({ defaultValues, onSubmit, onCancel, submitting }: AgeGroupFormProps) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AgeGroupFormData>({
    resolver: zodResolver(ageGroupFormSchema),
    defaultValues: {
      label: '',
      method: 'custom',
      minAge: null,
      maxAge: null,
      calendarColor: DEFAULT_CALENDAR_COLOR,
      sortOrder: 0,
      isActive: true,
      ...defaultValues,
    },
  });

  const method = watch('method');
  const label = watch('label');
  const calendarColor = watch('calendarColor');
  const minAge = watch('minAge');
  const maxAge = watch('maxAge');
  const selectedBallPreset =
    method === 'ball_color' ? matchTennisBallColorPreset(label ?? '', calendarColor ?? '') : null;
  const ageRangeUsesAdultColor =
    method === 'age_range' ? ageRangeIncludesAdultAges(minAge, maxAge) : false;

  useEffect(() => {
    if (method !== 'age_range') return;
    setValue('calendarColor', resolveAgeRangeCalendarColor(minAge, maxAge), {
      shouldValidate: true,
    });
  }, [method, minAge, maxAge, setValue]);

  const applyBallColorPreset = (presetKey: TennisBallColorPresetKey) => {
    const preset = TENNIS_BALL_COLOR_PRESETS.find((item) => item.key === presetKey);
    if (!preset) return;
    setValue('label', preset.label, { shouldValidate: true, shouldDirty: true });
    setValue('calendarColor', preset.calendarColor, { shouldValidate: true, shouldDirty: true });
  };

  // Must not use a nested <form>: AgeGroupsSettingsTab renders inside AcademyManagement's
  // outer <form>, and nested forms are invalid—submit buttons would submit the academy form.
  const submitForm = handleSubmit(async (data) => {
    await onSubmit(data);
  });

  return (
    <div
      className="space-y-4"
      onKeyDownCapture={(e) => {
        if (e.key !== 'Enter') return;
        if (e.target instanceof HTMLInputElement) {
          e.preventDefault();
          e.stopPropagation();
          void submitForm();
        }
      }}
    >
      <Field data-invalid={!!errors.label}>
        <FieldLabel htmlFor="ag-label">Label *</FieldLabel>
        <Input
          id="ag-label"
          {...register('label')}
          placeholder='e.g. "QuickStart (5-8)" or "Red Ball"'
          aria-invalid={!!errors.label}
        />
        <FieldError>{errors.label?.message}</FieldError>
      </Field>

      <Field>
        <FieldLabel>Method *</FieldLabel>
        <Controller
          name="method"
          control={control}
          render={({ field }) => (
            <div className="grid grid-cols-3 gap-2">
              {(['age_range', 'ball_color', 'custom'] as AgeGroupMethod[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => field.onChange(m)}
                  className={cn(
                    'rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    field.value === m
                      ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:border-primary-500 dark:text-primary-300'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600'
                  )}
                >
                  {METHOD_LABELS[m]}
                </button>
              ))}
            </div>
          )}
        />
        {method === 'custom' && (
          <FieldDescription>
            Free-form labels for any grouping (e.g. High Performance, U12 squad).
          </FieldDescription>
        )}
        {method === 'age_range' && (
          <FieldDescription>
            Calendar colour is set automatically — junior (under 13) or adult (13 and older).
          </FieldDescription>
        )}
        <FieldError>{errors.method?.message}</FieldError>
      </Field>

      {method === 'ball_color' && (
        <Field>
          <FieldLabel>Ball colour *</FieldLabel>
          <FieldDescription>
            Lessons in this group appear on the schedule and calendar using the colour for each ball.
          </FieldDescription>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {TENNIS_BALL_COLOR_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                onClick={() => applyBallColorPreset(preset.key)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  selectedBallPreset === preset.key
                    ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:border-primary-500 dark:text-primary-300'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600'
                )}
              >
                <span
                  className="h-4 w-4 shrink-0 rounded-full border border-black/10"
                  style={{ backgroundColor: preset.calendarColor }}
                  aria-hidden
                />
                {preset.shortLabel}
              </button>
            ))}
          </div>
        </Field>
      )}

      {method === 'age_range' && (
        <div className="grid grid-cols-2 gap-4">
          <Field data-invalid={!!errors.minAge}>
            <FieldLabel htmlFor="ag-min-age">Min Age *</FieldLabel>
            <Controller
              name="minAge"
              control={control}
              render={({ field }) => (
                <Input
                  id="ag-min-age"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="e.g. 5"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    field.onChange(val === '' ? null : parseInt(val, 10));
                  }}
                  aria-invalid={!!errors.minAge}
                />
              )}
            />
            <FieldError>{errors.minAge?.message}</FieldError>
          </Field>
          <Field data-invalid={!!errors.maxAge}>
            <FieldLabel htmlFor="ag-max-age">Max Age *</FieldLabel>
            <Controller
              name="maxAge"
              control={control}
              render={({ field }) => (
                <Input
                  id="ag-max-age"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="e.g. 12"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    field.onChange(val === '' ? null : parseInt(val, 10));
                  }}
                  aria-invalid={!!errors.maxAge}
                />
              )}
            />
            <FieldError>{errors.maxAge?.message}</FieldError>
          </Field>
        </div>
      )}

      {method === 'age_range' && (
        <Field>
          <FieldLabel>Calendar colour</FieldLabel>
          <FieldDescription>
            Based on the age range — groups that include anyone aged 13 or older use the adult colour.
          </FieldDescription>
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900/40">
            <span
              className="h-4 w-4 shrink-0 rounded-full border border-black/10"
              style={{ backgroundColor: calendarColor ?? DEFAULT_CALENDAR_COLOR }}
              aria-hidden
            />
            <span className="font-medium text-gray-900 dark:text-white">
              {ageRangeUsesAdultColor ? 'Adult' : 'Junior'}
            </span>
          </div>
        </Field>
      )}

      {method === 'custom' && (
      <Field data-invalid={!!errors.calendarColor}>
        <FieldLabel htmlFor="ag-calendar-color">Calendar colour *</FieldLabel>
        <FieldDescription>Used on schedule and calendar views for lessons in this group.</FieldDescription>
        <Controller
          name="calendarColor"
          control={control}
          render={({ field }) => (
            <div className="flex flex-wrap items-center gap-3">
              <input
                id="ag-calendar-color"
                type="color"
                value={field.value ?? DEFAULT_CALENDAR_COLOR}
                onChange={(e) => field.onChange(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded border border-gray-200 dark:border-gray-700 bg-transparent"
                aria-label="Calendar colour"
              />
              <Input
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder="#22c55e"
                className="w-32 font-mono text-sm"
                aria-invalid={!!errors.calendarColor}
              />
            </div>
          )}
        />
        <FieldError>{errors.calendarColor?.message}</FieldError>
      </Field>
      )}

      <Field data-invalid={!!errors.sortOrder}>
        <FieldLabel htmlFor="ag-sort-order">Sort Order</FieldLabel>
        <FieldDescription>Lower numbers appear first in lists.</FieldDescription>
        <Controller
          name="sortOrder"
          control={control}
          render={({ field }) => (
            <Input
              id="ag-sort-order"
              type="number"
              min={0}
              className="w-24"
              value={field.value ?? 0}
              onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
            />
          )}
        />
        <FieldError>{errors.sortOrder?.message}</FieldError>
      </Field>

      <div className="flex items-center gap-3">
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Switch
              id="ag-is-active"
              checked={!!field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <label htmlFor="ag-is-active" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          Active
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" isLoading={submitting} onClick={() => void submitForm()}>
          Save Group
        </Button>
      </div>
    </div>
  );
}

export function AgeGroupsSettingsTab({ academy }: AgeGroupsSettingsTabProps) {
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AgeGroup | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [seedMethod, setSeedMethod] = useState<AgeGroupMethod>('age_range');
  const [seeding, setSeeding] = useState(false);
  const [showSeedPanel, setShowSeedPanel] = useState(false);

  const syncAgeGroupCache = () => {
    invalidateAgeGroupsQueries(academy.id);
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      const groups = await ageGroupService.getAgeGroups(academy.id, true);
      setAgeGroups(groups.sort((a, b) => a.sortOrder - b.sortOrder));
      syncAgeGroupCache();
    } catch {
      toast.error('Failed to load age groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [academy.id]);

  const handleCreate = async (data: AgeGroupFormData) => {
    try {
      setSubmitting(true);
      await ageGroupService.createAgeGroup(academy.id, data);
      toast.success('Age group created');
      setShowCreateModal(false);
      await loadGroups();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create age group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (data: AgeGroupFormData) => {
    if (!editingGroup) return;
    try {
      setSubmitting(true);
      await ageGroupService.updateAgeGroup(editingGroup.id, academy.id, data);
      toast.success('Age group updated');
      setEditingGroup(null);
      await loadGroups();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update age group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (group: AgeGroup) => {
    if (!confirm(`Delete "${group.label}"? This cannot be undone.`)) return;
    try {
      setDeletingId(group.id);
      await ageGroupService.deleteAgeGroup(group.id, academy.id);
      toast.success('Age group deleted');
      await loadGroups();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete age group');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (group: AgeGroup) => {
    try {
      await ageGroupService.updateAgeGroup(group.id, academy.id, {
        isActive: !group.isActive,
      });
      setAgeGroups((prev) =>
        prev.map((g) => (g.id === group.id ? { ...g, isActive: !g.isActive } : g))
      );
      syncAgeGroupCache();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update age group');
    }
  };

  const handleReorder = async (index: number, direction: 'up' | 'down') => {
    const newGroups = [...ageGroups];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newGroups.length) return;

    [newGroups[index], newGroups[swapIndex]] = [newGroups[swapIndex], newGroups[index]];

    const updated = newGroups.map((g, i) => ({ ...g, sortOrder: i }));
    setAgeGroups(updated);

    try {
      const refreshed = await ageGroupService.reorderAgeGroups(
        academy.id,
        updated.map((g) => g.id)
      );
      setAgeGroups(refreshed.sort((a, b) => a.sortOrder - b.sortOrder));
      syncAgeGroupCache();
    } catch {
      toast.error('Failed to save order');
      await loadGroups();
    }
  };

  const handleSeed = async () => {
    try {
      setSeeding(true);
      const seeded = await ageGroupService.seedAgeGroups(academy.id, seedMethod);
      toast.success(`Created ${seeded.length} default age groups`);
      setShowSeedPanel(false);
      await loadGroups();
    } catch (err: any) {
      toast.error(err.message || 'Failed to seed age groups');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Age Groups</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Define custom student groupings for your academy. These replace the default
                juniors/adults/all grouping when assigning lessons.
              </p>
            </div>
            {!loading && ageGroups.length > 0 && (
              <Button
                type="button"
                size="sm"
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add Group
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : ageGroups.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 p-8 text-center">
              <Layers className="mx-auto mb-3 h-10 w-10 text-gray-400" />
              <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                No age groups configured
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                Seed a set of defaults to get started quickly, or create groups manually.
              </p>

              {!showSeedPanel ? (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSeedPanel(true)}
                  >
                    Seed defaults
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    Create manually
                  </Button>
                </div>
              ) : (
                <div className="max-w-md mx-auto text-left space-y-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Choose a grouping method to seed:
                  </p>
                  <div className="space-y-2">
                    {SEED_METHOD_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSeedMethod(opt.value)}
                        className={cn(
                          'w-full text-left rounded-lg border-2 p-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                          seedMethod === opt.value
                            ? 'border-primary-600 bg-primary-50 dark:bg-primary-950/40 dark:border-primary-500'
                            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                        )}
                      >
                        <p
                          className={cn(
                            'text-sm font-medium',
                            seedMethod === opt.value
                              ? 'text-primary-700 dark:text-primary-300'
                              : 'text-gray-900 dark:text-white'
                          )}
                        >
                          {opt.label}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {opt.description}
                        </p>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSeedPanel(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      isLoading={seeding}
                      onClick={handleSeed}
                    >
                      Seed defaults
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {ageGroups.map((group, index) => (
                <div
                  key={group.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
                    group.isActive
                      ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 opacity-60'
                  )}
                >
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleReorder(index, 'up')}
                      disabled={index === 0}
                      className="rounded p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed dark:hover:text-gray-300"
                      aria-label="Move up"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReorder(index, 'down')}
                      disabled={index === ageGroups.length - 1}
                      className="rounded p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20 disabled:cursor-not-allowed dark:hover:text-gray-300"
                      aria-label="Move down"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <span
                    className="h-4 w-4 shrink-0 rounded-full border border-black/10"
                    style={{ backgroundColor: group.calendarColor }}
                    aria-hidden
                  />

                  {/* Label + meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                        {group.label}
                      </span>
                      <AgeGroupMethodBadge method={group.method} />
                      {group.method === 'age_range' &&
                        group.minAge != null &&
                        group.maxAge != null && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {group.minAge}–{group.maxAge} yrs
                          </span>
                        )}
                    </div>
                  </div>

                  {/* Active toggle */}
                  <Switch
                    checked={group.isActive}
                    onCheckedChange={() => handleToggleActive(group)}
                    aria-label={`${group.isActive ? 'Deactivate' : 'Activate'} ${group.label}`}
                  />

                  {/* Edit / Delete */}
                  <button
                    type="button"
                    onClick={() => setEditingGroup(group)}
                    className="rounded p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    aria-label={`Edit ${group.label}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(group)}
                    disabled={deletingId === group.id}
                    className="rounded p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-40"
                    aria-label={`Delete ${group.label}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add Age Group"
        maxWidth="md"
      >
        <AgeGroupForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          submitting={submitting}
        />
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        title="Edit Age Group"
        maxWidth="md"
      >
        {editingGroup && (
          <AgeGroupForm
            defaultValues={{
              label: editingGroup.label,
              method: editingGroup.method,
              minAge: editingGroup.minAge ?? null,
              maxAge: editingGroup.maxAge ?? null,
              calendarColor: editingGroup.calendarColor,
              sortOrder: editingGroup.sortOrder,
              isActive: editingGroup.isActive,
            }}
            onSubmit={handleUpdate}
            onCancel={() => setEditingGroup(null)}
            submitting={submitting}
          />
        )}
      </Modal>
    </div>
  );
}

export default AgeGroupsSettingsTab;
